import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { AccountInfo } from 'src/app/shared/models/accountInfo.model';
import { OperationMode } from 'src/app/shared/models/enum.model';
import { SetTitle } from 'src/app/shared/ngxs/action/account.action';
import { AccountState } from 'src/app/shared/ngxs/state/account.state';
import { PeriodFilter } from './../../shared/models/period-filter.model';
import { PeriodModel } from './../../shared/models/period.model';
import {
  FilterPeriods,
  RemovePeriod,
} from './../../shared/ngxs/action/period.action';
import { PeriodState } from './../../shared/ngxs/state/period.state';
import { ConversionService } from './../../shared/services/conversion.service';
import { FilterService } from './../../shared/services/filter.service';
import { ManagePeriodComponent } from './../manage-period/manage-period.component';

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD MMM YYYY',
  },
  display: {
    dateInput: 'DD MMM YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-periods',
  templateUrl: './periods.component.html',
  styleUrls: ['./periods.component.scss'],
  providers: [
    FilterService,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class PeriodsComponent implements OnInit, OnDestroy {
  subscription = new Subscription();
  @ViewChild('paginator')
  paginator: MatPaginator;

  @Select(PeriodState.periods)
  periods$: Observable<PeriodModel[]>;

  @Select(PeriodState.periodsTotal)
  periodsTotal$: Observable<number>;

  @Select(AccountState.account)
  account$: Observable<AccountInfo>;

  isAdministrator = false;

  columnsToDisplay: string[] = ['name', 'from', 'to', 'actions'];
  dataSource = new MatTableDataSource<PeriodModel>();
  pageSize = 5;
  pageSizeOptions = [5, 10, 25, 100];
  total = 0;

  nameForm = new FormControl();
  fromForm = new FormControl();
  toForm = new FormControl();

  constructor(
    private dialog: MatDialog,
    private store: Store,
    private translateService: TranslateService,
    private filterService: FilterService,
    private conversionService: ConversionService
  ) {
    this.translateService.get('periods').subscribe((x) => {
      this.store.dispatch(new SetTitle(x));
    });
  }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;

    this.subscription.add(
      this.translateService.onLangChange.subscribe((x) => {
        if (x) {
          this.store.dispatch(
            new FilterPeriods(
              this.filterService.generateHttpParams(),
              this.getFilter()
            )
          );
        }
      })
    );

    this.store.dispatch(
      new FilterPeriods(
        this.filterService.generateHttpParams(),
        this.getFilter()
      )
    );

    this.subscribeEvents();
  }

  subscribeEvents() {
    this.subscription.add(
      this.periods$.subscribe((x) => {
        this.dataSource = new MatTableDataSource<PeriodModel>(x);
      })
    );

    this.subscription.add(
      this.periodsTotal$.subscribe((x) => {
        if (x) {
          this.total = x;
        }
      })
    );
    this.subscription.add(
      this.account$.subscribe((x) => {
        if (x?.userType.toString() === 'Administrator') {
          this.isAdministrator = true;
        }
      })
    );
  }

  deletePeriod(model: PeriodModel) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete Period',
        text: 'Are you sure you want to delete this period?',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.store.dispatch(new RemovePeriod(model.id));
      }
    });
  }

  addPeriod() {
    const dialogRef = this.dialog.open(ManagePeriodComponent, {
      data: {
        mode: OperationMode.Create,
      },
    });
  }

  editPeriod(model: PeriodModel) {
    const dialogRef = this.dialog.open(ManagePeriodComponent, {
      data: {
        model: model,
        mode: OperationMode.Edit,
      },
    });
  }

  previewBuildingsInPeriod(event: any) {}

  setAcceptFiles() {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }

  onFileChange(event: any) {
    if (event.target.files && event.target.files.length) {
      const reader = new FileReader();
      const [file] = event.target.files;
      reader.onload = () => {
        var base64Data = reader.result.toString();

        // this.store.dispatch(new ImportPeriods(base64Data));
      };
      // Read the file
      reader.readAsDataURL(file);
    }
  }

  getFilter() {
    const filter = new PeriodFilter();
    filter.name = this.nameForm.value;
    filter.from =
      this.fromForm && this.fromForm.value
        ? this.getDateForInsert(this.fromForm.value.toDate())
        : '';
    filter.to =
      this.toForm && this.toForm.value
        ? this.getDateForInsert(this.toForm.value.toDate())
        : '';
    return filter;
  }

  getDateForInsert(data: Date) {
    return data ? this.conversionService.getDateFormatForInsert(data) : '';
  }

  filterPeriods() {
    this.store.dispatch(
      new FilterPeriods(
        this.filterService.generateHttpParams(),
        this.getFilter()
      )
    );
  }

  clearFilter() {
    this.clear();

    this.store.dispatch(
      new FilterPeriods(
        this.filterService.generateHttpParams(),
        this.getFilter()
      )
    );
  }

  clear() {
    this.nameForm.setValue('');
    this.fromForm.setValue(undefined);
    this.toForm.setValue(undefined);
  }

  changePage(event: PageEvent) {
    this.filterService.page = event;

    this.store.dispatch(
      new FilterPeriods(
        this.filterService.generateHttpParams(),
        this.getFilter()
      )
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
